import type { PieceId } from '../game/building';
import { BUILD_TIERS, TIER_LABEL, type BuildTier } from '../engine/materials';
import { RadialWheel, type WheelSegment } from './radialwheel';

// Build menu. A Rust-style RADIAL WHEEL: hold RIGHT MOUSE to open, move the mouse to hover a segment,
// release to select (clicking a segment also selects). The five material tiers are REAL SLICES on the
// ring alongside the pieces (owner ask 2026-07-10) -- they used to be swatches drawn under the wheel,
// which the first-person movement-mode cursor could never reach (it only picks ring segments by angle),
// so tier could not be changed at all in first person.
//
// API: show() / hide() open and close the overlay; commitSelection() applies whatever is hovered --
// a tier slice switches material (keeping the active piece), a piece slice switches piece (keeping
// the tier). Both stay highlighted between opens via isSegActive.

type BuildKey = PieceId | BuildTier;

const PIECES: Array<WheelSegment<BuildKey>> = [
  { key: 'foundation_square', label: 'Square\nFoundation' },
  { key: 'foundation_triangle', label: 'Triangle\nFoundation' },
  { key: 'wall', label: 'Wall' },
  { key: 'half_wall', label: 'Half Wall' },
  { key: 'doorway', label: 'Doorway' },
  { key: 'double_doorway', label: 'Double\nDoorway' },
  { key: 'window', label: 'Window' },
  { key: 'pillar', label: 'Pillar' },
  { key: 'ceiling_square', label: 'Square\nCeiling' },
  { key: 'ceiling_triangle', label: 'Triangle\nCeiling' },
  { key: 'stairs', label: 'Stairs' },
];

// OWNER 2026-07-11: the material tiers are the INNER RING (between hub and the piece ring), each slice
// skinned with its real material texture -- the outer ring is pieces only.
const TIER_SEGS: Array<WheelSegment<BuildKey>> = BUILD_TIERS.map((t) => ({ key: t, label: TIER_LABEL[t], tier: t }));

const TIER_KEYS = new Set<string>(BUILD_TIERS);

export class BuildWheel extends RadialWheel<BuildKey, never> {
  private activePiece: PieceId = 'foundation_square';
  private activeTier: BuildTier = 'twig';

  constructor(
    private readonly onSelect: (id: PieceId) => void,
    private readonly onSelectTier: (t: BuildTier) => void,
  ) {
    super({
      segments: PIECES,
      innerSegments: TIER_SEGS,
      activeSeg: null, // active state is the piece+tier pair below (isSegActive), not the base's single key
      hubFallbackLabel: 'BUILD',
      onPointerCommit: () => { this.commitSelection(); this.hide(); },
    });
  }

  // Both the chosen piece AND the chosen material stay lit between opens.
  protected override isSegActive(key: BuildKey): boolean {
    return key === this.activePiece || key === this.activeTier;
  }

  // Live cost + affordability on the tier slices (owner QoL 2026-07-12): each material shows what a
  // piece costs and greys when the resources aren't in your inventory -- refreshed at every open.
  refreshTiers(state: Partial<Record<BuildTier, { cost: string; enabled: boolean }>>): void {
    for (const t of BUILD_TIERS) {
      const s = state[t];
      if (s) this.setSegState(t, { sub: s.cost, disabled: !s.enabled });
    }
  }

  // Apply whatever the cursor is hovering. Selecting a tier keeps the activePiece intact; selecting
  // a piece keeps the tier. Safe to call with nothing hovered (no-op).
  commitSelection(): void {
    this.commit({
      onSeg: (key) => {
        if (TIER_KEYS.has(key)) {
          this.activeTier = key as BuildTier;
          this.onSelectTier(this.activeTier);
        } else {
          this.activePiece = key as PieceId;
          this.onSelect(this.activePiece);
        }
        this.repaint();
      },
      stickySeg: false,
    });
  }
}
